import React, { useCallback, useEffect, useState } from 'react';
import { formatRetryRemaining, JIRA_RATE_LIMIT_EVENT } from '../utils/jiraErrors';

interface Props {
  onRetry?: () => void;
  className?: string;
  compact?: boolean;
}

const JiraRateLimitBanner: React.FC<Props> = ({ onRetry, className = '', compact }) => {
  const [message, setMessage] = useState<string | null>(null);
  const [retryAt, setRetryAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent).detail;
      const msg = detail?.message || 'Jira rate limit — zkuste to později.';
      setMessage(msg);
      if (typeof detail?.retryAt === 'number' && detail.retryAt > Date.now()) {
        setRetryAt(detail.retryAt);
      } else {
        setRetryAt(null);
      }
    };
    window.addEventListener(JIRA_RATE_LIMIT_EVENT, handler);
    return () => window.removeEventListener(JIRA_RATE_LIMIT_EVENT, handler);
  }, []);

  // Live countdown when we know unblock time
  useEffect(() => {
    if (!retryAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [retryAt]);

  const dismiss = useCallback(() => {
    setMessage(null);
    setRetryAt(null);
  }, []);

  if (!message && !retryAt) return null;

  const remaining =
    retryAt != null ? formatRetryRemaining(retryAt, now) : null;
  const expired = retryAt != null && retryAt <= now;

  return (
    <div
      className={`
        border border-amber-400/50 bg-amber-50 dark:bg-amber-950/50
        text-amber-950 dark:text-amber-100
        ${compact ? 'rounded-lg px-3 py-2 text-xs' : 'rounded-lg px-4 py-3 text-sm shadow-sm'}
        flex items-start justify-between gap-3
        ${className}
      `}
      role="alert"
    >
      <div className="min-w-0">
        {!compact && (
          <div className="font-semibold mb-0.5">Jira teď neodpovídá (rate limit)</div>
        )}
        <div className={compact ? 'font-medium' : ''}>
          {expired
            ? 'Rate limit by měl být pryč — zkus obnovit.'
            : remaining
              ? `Zbývá ${remaining} (odpočet z Retry-After, který poslala Jira).`
              : message}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {onRetry && (
          <button
            type="button"
            onClick={() => {
              dismiss();
              onRetry();
            }}
            className="px-2.5 py-1 rounded bg-amber-500/20 hover:bg-amber-500/30 text-xs font-medium"
          >
            Zkusit znovu
          </button>
        )}
        <button
          type="button"
          onClick={dismiss}
          className="text-amber-700/70 dark:text-amber-200/70 hover:text-amber-900 dark:hover:text-amber-50 px-1"
          title="Zavřít"
        >
          ×
        </button>
      </div>
    </div>
  );
};

/** Inline banner when you already have a local error string (page-level). */
export const JiraErrorBanner: React.FC<{
  message: string | null;
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
  retryAt?: number | null;
}> = ({ message, onRetry, onDismiss, className = '', retryAt = null }) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!retryAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [retryAt]);

  if (!message) return null;

  const remaining = retryAt ? formatRetryRemaining(retryAt, now) : null;

  return (
    <div
      className={`
        mb-4 rounded-lg border border-amber-400/50 bg-amber-50 dark:bg-amber-950/40
        px-4 py-3 text-sm text-amber-900 dark:text-amber-100
        flex items-start justify-between gap-3 ${className}
      `}
      role="alert"
    >
      <div>
        <div className="font-semibold mb-0.5">Jira teď neodpovídá</div>
        <div>
          {remaining && retryAt && retryAt > now
            ? `Zbývá ${remaining} (Retry-After z Jiry).`
            : message}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="px-3 py-1 rounded bg-amber-500/20 hover:bg-amber-500/30"
          >
            Zkusit znovu
          </button>
        )}
        {onDismiss && (
          <button type="button" onClick={onDismiss} className="px-1 opacity-70 hover:opacity-100">
            ×
          </button>
        )}
      </div>
    </div>
  );
};

export default JiraRateLimitBanner;
